import * as React from "react";
import { toast } from "sonner";
import authProvider, {
	type TwoFactorSetupResponse,
	type TwoFactorStatusResponse,
} from "src/providers/auth-provider";

interface UseTwoFactorReturn {
	status: TwoFactorStatusResponse | null;
	isLoading: boolean;
	isSubmitting: boolean;
	error: string | null;
	fetchStatus: () => Promise<void>;
	setupTwoFactor: () => Promise<TwoFactorSetupResponse | null>;
	enableTwoFactor: (
		code: string,
		secret: string,
		backupCodes: string[],
	) => Promise<boolean>;
	disableTwoFactor: (code: string) => Promise<boolean>;
	verifyTwoFactor: (code: string) => Promise<boolean>;
	regenerateBackupCodes: (code: string) => Promise<string[] | null>;
	clearError: () => void;
}

export const useTwoFactor = (): UseTwoFactorReturn => {
	const [status, setStatus] = React.useState<TwoFactorStatusResponse | null>(
		null,
	);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const fetchStatus = React.useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const data = await authProvider.get2FAStatus();
			setStatus(data);
		} catch {
			const message = "Failed to fetch 2FA status";
			setError(message);
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const setupTwoFactor = React.useCallback(async () => {
		try {
			setIsSubmitting(true);
			setError(null);
			const data = await authProvider.setup2FA();
			return data;
		} catch {
			const message = "Failed to setup 2FA";
			setError(message);
			toast.error(message);
			return null;
		} finally {
			setIsSubmitting(false);
		}
	}, []);

	const enableTwoFactor = React.useCallback(
		async (code: string, secret: string, backupCodes: string[]) => {
			try {
				setIsSubmitting(true);
				setError(null);
				await authProvider.enable2FA(code, secret, backupCodes);
				await fetchStatus();
				toast.success("Two-factor authentication enabled");
				return true;
			} catch {
				const message = "Invalid verification code";
				setError(message);
				toast.error(message);
				return false;
			} finally {
				setIsSubmitting(false);
			}
		},
		[fetchStatus],
	);

	const disableTwoFactor = React.useCallback(
		async (code: string) => {
			try {
				setIsSubmitting(true);
				setError(null);
				await authProvider.disable2FA(code);
				await fetchStatus();
				toast.success("Two-factor authentication disabled", {
					description: "You will be logged out for security reasons",
				});

				await authProvider.logout();
				window.location.href = "/login";

				return true;
			} catch {
				const message = "Invalid verification code";
				setError(message);
				toast.error(message);
				return false;
			} finally {
				setIsSubmitting(false);
			}
		},
		[fetchStatus],
	);

	const verifyTwoFactor = React.useCallback(async (code: string) => {
		try {
			setIsSubmitting(true);
			setError(null);
			await authProvider.verify2FA(code);
			return true;
		} catch {
			const message = "Invalid verification code";
			setError(message);
			toast.error(message);
			return false;
		} finally {
			setIsSubmitting(false);
		}
	}, []);

	const regenerateBackupCodes = React.useCallback(async (code: string) => {
		try {
			setIsSubmitting(true);
			setError(null);
			const data = await authProvider.regenerateBackupCodes(code);
			toast.success("Backup codes regenerated");
			return data.backupCodes;
		} catch {
			const message = "Failed to regenerate backup codes";
			setError(message);
			toast.error(message);
			return null;
		} finally {
			setIsSubmitting(false);
		}
	}, []);

	const clearError = React.useCallback(() => {
		setError(null);
	}, []);

	return {
		status,
		isLoading,
		isSubmitting,
		error,
		fetchStatus,
		setupTwoFactor,
		enableTwoFactor,
		disableTwoFactor,
		verifyTwoFactor,
		regenerateBackupCodes,
		clearError,
	};
};
